import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import "../../styles/admin/admin-detail.css";
import "../../styles/admin/admin-form.css";
import config from "../../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

export default function AdminAllyDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [ally, setAlly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [editForm, setEditForm] = useState({
    name: "",
    lastName: "",
    email: "",
    phone: "",
    dni: "",
    country: "CO",
  });

  useEffect(() => {
    loadAlly();
  }, [id]);

  const loadAlly = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/allies/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo cargar el detalle del aliado");
      }

      const data = responseText ? JSON.parse(responseText) : null;
      setAlly(data);
    } catch (err) {
      setError(err.message || "Ocurrió un error al cargar el aliado");
      setAlly(null);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = () => {
    if (!ally) return;

    setEditForm({
      name: ally.name || "",
      lastName: ally.lastName || "",
      email: ally.email || "",
      phone: ally.phone || "",
      dni: ally.dni || "",
      country: ally.country || "CO",
    });

    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSavingEdit(false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/allies/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editForm.name.trim(),
          lastName: editForm.lastName.trim(),
          email: editForm.email.trim().toLowerCase(),
          phone: editForm.phone.trim(),
          dni: editForm.dni.trim(),
          country: editForm.country.trim(),
        }),
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo actualizar el aliado");
      }

      await loadAlly();
      closeEditModal();
    } catch (err) {
      setError(err.message || "Ocurrió un error al actualizar el aliado");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteAlly = async () => {
    if (!ally) return;

    const confirmed = window.confirm(
      `¿Seguro que quieres eliminar a ${ally.name} ${ally.lastName || ""}? Esta acción no se puede deshacer.`
    );

    if (!confirmed) return;

    setDeleting(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/allies/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo eliminar el aliado");
      }

      navigate("/admin/aliados");
    } catch (err) {
      setError(err.message || "Ocurrió un error al eliminar el aliado");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-page-head">
          <div>
            <h2>Detalle de aliado</h2>
            <p>Cargando información...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !ally) {
    return (
      <div className="admin-page">
        <div className="admin-page-head">
          <div>
            <h2>Detalle de aliado</h2>
            <p>Información general del aliado comercial</p>
          </div>
        </div>

        <p className="admin-form-message error">{error}</p>
      </div>
    );
  }

  if (!ally) {
    return (
      <div className="admin-page">
        <div className="admin-page-head">
          <div>
            <h2>Detalle de aliado</h2>
            <p>No se encontró información del aliado</p>
          </div>
        </div>
      </div>
    );
  }

  const fullName = `${ally.name || ""} ${ally.lastName || ""}`.trim();

  return (
    <div className="admin-page">
      <div className="admin-page-head admin-detail-topbar">
        <div className="admin-detail-head-left">
          <button
            type="button"
            className="admin-back-arrow-btn"
            onClick={() => navigate("/admin/aliados")}
            title="Volver"
          >
            <ArrowLeft size={20} />
          </button>

          <div>
            <h2>Detalle de aliado</h2>
            <p>Información general del aliado comercial</p>
          </div>
        </div>

        <div className="admin-detail-actions">
          <button
            type="button"
            className="secondary-btn admin-action-btn"
            onClick={openEditModal}
          >
            <Pencil size={16} />
            <span>Editar</span>
          </button>

          <button
            type="button"
            className="admin-danger-btn"
            onClick={handleDeleteAlly}
            disabled={deleting}
          >
            <Trash2 size={16} />
            <span>{deleting ? "Eliminando..." : "Eliminar"}</span>
          </button>
        </div>
      </div>

      {error ? <p className="admin-form-message error">{error}</p> : null}

      <div className="detail-grid">
        <section className="detail-card">
          <h3>Datos personales</h3>
          <div className="detail-list">
            <p><strong>Nombre:</strong> {fullName || "-"}</p>
            <p><strong>Correo:</strong> {ally.email || "-"}</p>
            <p><strong>Celular:</strong> {ally.phone || "-"}</p>
            <p><strong>Cédula:</strong> {ally.dni || "-"}</p>
            <p><strong>País:</strong> {ally.country || "-"}</p>
          </div>
        </section>

        <section className="detail-card">
          <h3>Estado del usuario</h3>
          <div className="detail-list">
            <p><strong>Estado:</strong> {ally.status || "-"}</p>
            <p><strong>Suscripción activa:</strong> {ally.subscriptionActive ? "Sí" : "No"}</p>
            <p><strong>Confirmación de aliado:</strong> {ally.userType === "ALLY" ? "Sí" : "No"}</p>
          </div>
        </section>

        <section className="detail-card">
          <h3>Mascotas creadas</h3>
          <div className="detail-list">
            <p><strong>Total:</strong> {ally.petCount ?? 0}</p>
          </div>
        </section>
      </div>

      {showEditModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-card">
            <h3>Editar aliado</h3>

            <form onSubmit={handleSaveEdit}>
              <div className="admin-form-grid">
                <div className="form-group">
                  <label>Nombre</label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name}
                    onChange={handleEditChange}
                    disabled={savingEdit}
                  />
                </div>

                <div className="form-group">
                  <label>Apellido</label>
                  <input
                    type="text"
                    name="lastName"
                    value={editForm.lastName}
                    onChange={handleEditChange}
                    disabled={savingEdit}
                  />
                </div>

                <div className="form-group">
                  <label>Correo</label>
                  <input
                    type="email"
                    name="email"
                    value={editForm.email}
                    onChange={handleEditChange}
                    disabled={savingEdit}
                  />
                </div>

                <div className="form-group">
                  <label>Celular</label>
                  <input
                    type="text"
                    name="phone"
                    value={editForm.phone}
                    onChange={handleEditChange}
                    disabled={savingEdit}
                  />
                </div>

                <div className="form-group">
                  <label>Cédula</label>
                  <input
                    type="text"
                    name="dni"
                    value={editForm.dni}
                    onChange={handleEditChange}
                    disabled={savingEdit}
                  />
                </div>

                <div className="form-group">
                  <label>País</label>
                  <select
                    name="country"
                    value={editForm.country}
                    onChange={handleEditChange}
                    disabled={savingEdit}
                  >
                    <option value="CO">Colombia</option>
                    <option value="EC">Ecuador</option>
                  </select>
                </div>
              </div>

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={closeEditModal}
                  disabled={savingEdit}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="primary-btn"
                  disabled={savingEdit}
                >
                  {savingEdit ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}